var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from "react";
import { insightsApi } from "@api/enpoints/insights-api";
/**
 * Formats a time bucket string based on granularity for display
 */
const formatTimeBucket = (time, granularity) => {
    try {
        switch (granularity) {
            case "hour": {
                // Format: "2024-01-15T14" -> "Jan 15, 14:00"
                const hourDate = new Date(time + ":00:00Z");
                return hourDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                    ', ' + hourDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            }
            case "day": {
                // Format: "2024-01-15" -> "Jan 15"
                const dayDate = new Date(time + 'T00:00:00Z');
                return dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            case "week": {
                // Format: "2024-W03" -> "Week 3"
                const weekMatch = time.match(/W(\d+)/);
                return weekMatch ? `Week ${parseInt(weekMatch[1])}` : time;
            }
            case "month": {
                // Format: "2024-01" -> "Jan 2024"
                const monthDate = new Date(time + '-01T00:00:00Z');
                return monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }
            case "year":
                return time;
            default:
                return time;
        }
    }
    catch (_a) {
        return time;
    }
};
/**
 * Transforms aggregated temperature API response to chart data points
 */
const transformTemperatureData = (response) => {
    if (!(response === null || response === void 0 ? void 0 : response.data) || response.data.length === 0) {
        return [];
    }
    return response.data.map(bucket => {
        var _a;
        return ({
            time: formatTimeBucket(bucket.time, response.granularity),
            count: bucket.count,
            avgTemperature: (_a = bucket.avgTemperature) !== null && _a !== void 0 ? _a : 0,
            minTemperature: bucket.minTemperature,
            maxTemperature: bucket.maxTemperature,
            hasAlarms: false
        });
    });
};
/**
 * Transforms aggregated alarm API response to chart data points
 */
const transformAlarmData = (response) => {
    if (!(response === null || response === void 0 ? void 0 : response.data) || response.data.length === 0) {
        return [];
    }
    return response.data.map(bucket => ({
        time: formatTimeBucket(bucket.time, response.granularity),
        count: bucket.count,
        acknowledgedCount: bucket.acknowledgedCount,
        unacknowledgedCount: bucket.unacknowledgedCount
    }));
};
/**
 * Transforms aggregated alarm data to pie chart format
 */
const transformAlarmHandlingData = (response) => {
    if (!(response === null || response === void 0 ? void 0 : response.data) || response.data.length === 0) {
        return [];
    }
    const totals = response.data.reduce((acc, bucket) => ({
        acknowledged: acc.acknowledged + bucket.acknowledgedCount,
        unacknowledged: acc.unacknowledged + bucket.unacknowledgedCount
    }), { acknowledged: 0, unacknowledged: 0 });
    return [
        { name: "Acknowledged", value: totals.acknowledged, color: "#3B3561" },
        { name: "Unacknowledged", value: totals.unacknowledged, color: "#FFA726" }
    ];
};
/**
 * Transforms alarm type breakdown from API response
 */
const transformAlarmTypeBreakdown = (response) => {
    if (!(response === null || response === void 0 ? void 0 : response.alarmTypeBreakdown) || response.alarmTypeBreakdown.length === 0) {
        return [];
    }
    return response.alarmTypeBreakdown.map(item => ({
        alarmType: item.alarmType,
        alarmTypeName: item.alarmTypeName,
        count: item.count
    }));
};
/**
 * Marks temperature data points that have alarms in the same time bucket
 */
const markTemperatureAlarms = (tempData, alarmData) => {
    const alarmTimes = new Set(alarmData.filter(a => a.count > 0).map(a => a.time));
    return tempData.map(point => (Object.assign(Object.assign({}, point), { hasAlarms: alarmTimes.has(point.time) })));
};
/**
 * Extracts error message from various error types
 */
const getErrorMessage = (err) => {
    var _a, _b;
    if (err instanceof Error) {
        return err.message;
    }
    if (typeof err === 'object' && err !== null) {
        const anyErr = err;
        return ((_b = (_a = anyErr.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || anyErr.message || "Unknown error";
    }
    return "Unknown error";
};
/**
 * Hook for fetching and transforming insights data.
 * Each insight component has independent loading/error states.
 * Partial failures do not block other components from rendering.
 */
export const useInsightsData = (params) => {
    var _a, _b;
    // Separate state for each insight type
    const [temperature, setTemperature] = useState({
        data: [],
        loading: true,
        error: null
    });
    const [alarms, setAlarms] = useState({
        data: [],
        loading: true,
        error: null
    });
    const [alarmHandling, setAlarmHandling] = useState({
        data: [],
        loading: true,
        error: null
    });
    const [alarmTypeBreakdown, setAlarmTypeBreakdown] = useState([]);
    const [temperatureMetadata, setTemperatureMetadata] = useState(null);
    const [alarmMetadata, setAlarmMetadata] = useState(null);
    const prevParamsRef = useRef("");
    const hasInitialized = useRef(false);
    const isFetchingRef = useRef(false);
    const fetchData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        // Don't fetch if no access point is selected yet
        if (!(params === null || params === void 0 ? void 0 : params.accessPointUid)) {
            return;
        }
        if (isFetchingRef.current) {
            return;
        }
        const paramsKey = JSON.stringify({
            accessPointUid: params.accessPointUid,
            fromDate: ((_a = params === null || params === void 0 ? void 0 : params.fromDate) === null || _a === void 0 ? void 0 : _a.toISOString()) || null,
            toDate: ((_b = params === null || params === void 0 ? void 0 : params.toDate) === null || _b === void 0 ? void 0 : _b.toISOString()) || null,
        });
        if (hasInitialized.current && paramsKey === prevParamsRef.current) {
            return;
        }
        isFetchingRef.current = true;
        prevParamsRef.current = paramsKey;
        hasInitialized.current = true;
        // Set all to loading
        setTemperature(prev => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        setAlarms(prev => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        setAlarmHandling(prev => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        // Default to previous 1 month if no dates provided
        const toDate = (params === null || params === void 0 ? void 0 : params.toDate) || new Date();
        const fromDate = (params === null || params === void 0 ? void 0 : params.fromDate) || (() => {
            const date = new Date(toDate);
            date.setMonth(date.getMonth() - 1);
            return date;
        })();
        // Fetch temperature insights independently
        const fetchTemperature = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const response = yield insightsApi.getTemperatureInsights({
                    accessPointUid: params.accessPointUid,
                    fromDate: fromDate.toISOString(),
                    toDate: toDate.toISOString()
                });
                if (response.isSuccess && response.data) {
                    const transformedData = transformTemperatureData(response.data);
                    setTemperatureMetadata({
                        granularity: response.data.granularity,
                        accessPointUid: response.data.accessPointUid,
                        accessPointName: response.data.accessPointName
                    });
                    return { success: true, data: transformedData };
                }
                else {
                    throw new Error(response.message || "Failed to fetch temperature insights");
                }
            }
            catch (err) {
                const errorMessage = getErrorMessage(err);
                setTemperature({ data: [], loading: false, error: errorMessage });
                console.error("Temperature insights error:", err);
                return { success: false, data: [] };
            }
        });
        // Fetch alarm insights independently
        const fetchAlarms = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const response = yield insightsApi.getAlarmInsights({
                    accessPointUid: params.accessPointUid,
                    fromDate: fromDate.toISOString(),
                    toDate: toDate.toISOString()
                });
                if (response.isSuccess && response.data) {
                    const transformedData = transformAlarmData(response.data);
                    const handlingData = transformAlarmHandlingData(response.data);
                    const typeBreakdown = transformAlarmTypeBreakdown(response.data);
                    setAlarmMetadata({
                        granularity: response.data.granularity,
                        accessPointUid: response.data.accessPointUid,
                        accessPointName: response.data.accessPointName
                    });
                    setAlarmTypeBreakdown(typeBreakdown);
                    return { success: true, data: transformedData, handlingData };
                }
                else {
                    throw new Error(response.message || "Failed to fetch alarm insights");
                }
            }
            catch (err) {
                const errorMessage = getErrorMessage(err);
                setAlarms({ data: [], loading: false, error: errorMessage });
                setAlarmHandling({ data: [], loading: false, error: errorMessage });
                console.error("Alarm insights error:", err);
                return { success: false, data: [], handlingData: [] };
            }
        });
        // Execute both fetches in parallel (independent failures)
        const [tempResult, alarmResult] = yield Promise.all([
            fetchTemperature(),
            fetchAlarms()
        ]);
        // Process temperature results
        if (tempResult.success) {
            let tempData = tempResult.data;
            // Mark temperature points with alarms if alarm data is available
            if (alarmResult.success && alarmResult.data.length > 0) {
                tempData = markTemperatureAlarms(tempData, alarmResult.data);
            }
            setTemperature({ data: tempData, loading: false, error: null });
        }
        // Process alarm results
        if (alarmResult.success) {
            setAlarms({ data: alarmResult.data, loading: false, error: null });
            setAlarmHandling({ data: alarmResult.handlingData || [], loading: false, error: null });
        }
        isFetchingRef.current = false;
    }), [
        params === null || params === void 0 ? void 0 : params.accessPointUid,
        (_a = params === null || params === void 0 ? void 0 : params.fromDate) === null || _a === void 0 ? void 0 : _a.getTime(),
        (_b = params === null || params === void 0 ? void 0 : params.toDate) === null || _b === void 0 ? void 0 : _b.getTime()
    ]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    // Overall loading: true if any component is still loading
    const loading = temperature.loading || alarms.loading || alarmHandling.loading;
    return {
        temperature,
        alarms,
        alarmHandling,
        alarmTypeBreakdown,
        temperatureMetadata,
        alarmMetadata,
        loading,
        refetchData: fetchData
    };
};
